import React from "react";
import { preventNegativeValueInDocs } from "_helpers/helpers";

const SCHED1_CAP = 20000;

function parseSchedMoney(value) {
  if (value === "" || value == null) return null;
  const n = parseFloat(String(value).replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
}

function nz(n) {
  return n === null || Number.isNaN(n) ? 0 : n;
}

function fmtSchedMoney(n) {
  if (n === null || Number.isNaN(n)) return "";
  return n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function DollarInput({
  id,
  field,
  value,
  onChange,
  disabled,
  error,
  showDollar = true,
}) {
  return (
    <div className="state-tax-nc4-sched1-dollar">
      {showDollar ? <span aria-hidden>$</span> : null}
      <input
        type="number"
        id={id}
        name={id}
        field={field}
        className={`form-control state-tax-nc4-sched1-control ${error ? "error" : ""}`}
        value={value ?? ""}
        onChange={onChange}
        disabled={disabled}
        placeholder="0.00"
        onWheel={(e) => e.target.blur()}
        onKeyDown={preventNegativeValueInDocs}
      />
    </div>
  );
}

function ReadOnlyDollar({ value, showDollar = true }) {
  return (
    <div className="state-tax-nc4-sched1-dollar state-tax-nc4-sched1-dollar--readonly">
      {showDollar ? <span aria-hidden>$</span> : null}
      <input
        type="text"
        className="form-control state-tax-nc4-sched1-control"
        readOnly
        tabIndex={-1}
        value={value}
        style={{ background: "#f2f2f2", pointerEvents: "none" }}
      />
    </div>
  );
}

/**
 * Schedule 1 — Estimated N.C. Itemized Deductions (NC-4 page 5).
 */
export default function StateTaxNc4Schedule1({
  form,
  errors,
  onFormChange,
  onSchedule1Skip,
}) {
  const skipped = !!form.nc4_schedule1_skip;

  const r1 = parseSchedMoney(form.nc4_sched1_home_mortgage);
  const r2 = parseSchedMoney(form.nc4_sched1_real_estate_taxes);
  const hasR1 = String(form.nc4_sched1_home_mortgage ?? "").trim() !== "";
  const hasR2 = String(form.nc4_sched1_real_estate_taxes ?? "").trim() !== "";
  const anyMortgage = hasR1 || hasR2;
  const sumMort = nz(r1) + nz(r2);
  const r3val = Math.min(sumMort, SCHED1_CAP);

  const r4 = parseSchedMoney(form.nc4_sched1_charitable);
  const r5 = parseSchedMoney(form.nc4_sched1_medical);
  const r6 = parseSchedMoney(form.nc4_sched1_repayment_claim);
  const hasR4 = String(form.nc4_sched1_charitable ?? "").trim() !== "";
  const hasR5 = String(form.nc4_sched1_medical ?? "").trim() !== "";
  const hasR6 = String(form.nc4_sched1_repayment_claim ?? "").trim() !== "";
  const anyOther = hasR4 || hasR5 || hasR6;

  const r3Display = skipped
    ? fmtSchedMoney(0)
    : anyMortgage
      ? fmtSchedMoney(r3val)
      : "";

  const r7sum = (skipped ? 0 : r3val) + nz(r4) + nz(r5) + nz(r6);
  const r7Display =
    skipped || anyMortgage || anyOther
      ? fmtSchedMoney(r7sum)
      : "";

  return (
    <div className="row state-tax-nc4-w4 state-tax-nc4-sched1">
      <div className="col-md-12">
        <p className="state-tax-nc4-sched1-important">
          <strong>Important:</strong> If you cannot reasonably estimate the
          amount to enter in the schedules below, you should enter{" "}
          <strong>ZERO (0)</strong> on Form NC-4, Line 1.
        </p>

        <header className="state-tax-nc4-sched1-header">
          <span className="state-tax-nc4-sched1-header-left">Schedule 1</span>
          <span className="state-tax-nc4-sched1-header-center">
            Estimated N.C. Itemized Deductions
          </span>
          <span className="state-tax-nc4-sched1-header-right" aria-hidden />
        </header>

        <hr className="state-tax-nc4-sched1-hr" />

        <div className={`state-tax-nc4-sched1-body ${skipped ? "state-tax-nc4-sched1-body--skipped" : ""}`}>
          <div className="state-tax-nc4-sched1-row">
            <span className="state-tax-nc4-sched1-label">Home Mortgage Interest</span>
            <DollarInput
              id="nc4_sched1_home_mortgage"
              field="form.nc4_sched1_home_mortgage"
              value={form.nc4_sched1_home_mortgage}
              onChange={onFormChange}
              disabled={skipped}
              error={errors["form.nc4_sched1_home_mortgage"]}
              showDollar={false}
            />
          </div>
          <div className="state-tax-nc4-sched1-row">
            <span className="state-tax-nc4-sched1-label">
              Real Estate Property Taxes
            </span>
            <DollarInput
              id="nc4_sched1_real_estate_taxes"
              field="form.nc4_sched1_real_estate_taxes"
              value={form.nc4_sched1_real_estate_taxes}
              onChange={onFormChange}
              disabled={skipped}
              error={errors["form.nc4_sched1_real_estate_taxes"]}
              showDollar={false}
            />
          </div>

          <hr className="state-tax-nc4-sched1-hr state-tax-nc4-sched1-hr--sub" />

          <div className="state-tax-nc4-sched1-row state-tax-nc4-sched1-row--subtotal">
            <span className="state-tax-nc4-sched1-label">
              Total Home Mortgage Interest and Real Estate Property Taxes*
            </span>
            <ReadOnlyDollar value={r3Display} showDollar={false} />
          </div>

          <div className="state-tax-nc4-sched1-row">
            <span className="state-tax-nc4-sched1-label">
              Charitable Contributions (Same as allowed for federal purposes)
            </span>
            <DollarInput
              id="nc4_sched1_charitable"
              field="form.nc4_sched1_charitable"
              value={form.nc4_sched1_charitable}
              onChange={onFormChange}
              disabled={skipped}
              error={errors["form.nc4_sched1_charitable"]}
              showDollar={false}
            />
          </div>
          <div className="state-tax-nc4-sched1-row">
            <span className="state-tax-nc4-sched1-label">
              Medical and Dental Expenses (Same as allowed for federal purposes)
            </span>
            <DollarInput
              id="nc4_sched1_medical"
              field="form.nc4_sched1_medical"
              value={form.nc4_sched1_medical}
              onChange={onFormChange}
              disabled={skipped}
              error={errors["form.nc4_sched1_medical"]}
              showDollar={false}
            />
          </div>
          <div className="state-tax-nc4-sched1-row">
            <span className="state-tax-nc4-sched1-label">
              Repayment of Claim of Right Income
            </span>
            <DollarInput
              id="nc4_sched1_repayment_claim"
              field="form.nc4_sched1_repayment_claim"
              value={form.nc4_sched1_repayment_claim}
              onChange={onFormChange}
              disabled={skipped}
              error={errors["form.nc4_sched1_repayment_claim"]}
              showDollar={false}
            />
          </div>

          <hr className="state-tax-nc4-sched1-hr state-tax-nc4-sched1-hr--double" />

          <div className="state-tax-nc4-sched1-row state-tax-nc4-sched1-row--total">
            <span className="state-tax-nc4-sched1-label">
              Total Estimated N.C. Itemized Deductions. Enter on Page 2, Part
              II, Line 1
            </span>
            <ReadOnlyDollar value={r7Display} showDollar={false} />
          </div>
        </div>

        <hr className="state-tax-nc4-sched1-hr" />

        <p className="state-tax-nc4-sched1-footnote">
          *The sum of your home mortgage interest and real estate property taxes
          may not exceed $20,000. For married taxpayers, the $20,000 limitation
          applies to the combined total of home mortgage interest and real
          estate property taxes claimed by both spouses, rather than to each
          spouse separately.
        </p>
      </div>
    </div>
  );
}
